import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/router';
import BreadcrumbItem from '@components/Breadcrumbs/BreadcrumbItem';
import BreadcrumbDropdown from '@components/Breadcrumbs/BreadcrumbDropdown';
import { IBreadcrumbStep } from '@typescript/index';
import styles from '@components/Breadcrumbs/Breadcrumbs.style';
import ErrorBoundary from '@components/errors/ErrorBoundary';
type PropsType = {
  steps: IBreadcrumbStep[];
};
const Breadcrumbs = ({
  steps
}: PropsType): JSX.Element => {
  const router = useRouter();
  const [activeDropdown, setActiveDropdown] = useState<IBreadcrumbStep | null>(null);
  const [leftPosition, setLeftPosition] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const breadcrumbsRootRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  useEffect(() => {
    setActiveDropdown(null);
  }, [router.asPath]);
  useEffect(() => {
    const checkTouchDevice = () => {
      if (typeof window === 'undefined') return false;
      return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || window.matchMedia('(pointer: coarse)').matches;
    };
    const updateTouchDevice = () => {
      setIsTouchDevice(checkTouchDevice());
    };
    updateTouchDevice();
    window.addEventListener('resize', updateTouchDevice);
    return () => {
      window.removeEventListener('resize', updateTouchDevice);
    };
  }, []);
  const updateDropdownPosition = (stepId: number) => {
    const dropdownRect = dropdownRef.current?.getBoundingClientRect();
    const containerRect = document.querySelector(`.breadcrumb-${stepId}`)?.getBoundingClientRect();
    if (containerRect && dropdownRect) {
      const viewportWidth = window.innerWidth;
      const dropdownWidth = dropdownRect.width;
      let newPosition = containerRect.left;
      if (containerRect.left + dropdownWidth > viewportWidth) {
        const outsideValue = containerRect.left + dropdownWidth - viewportWidth;
        newPosition -= outsideValue + 8;
      }
      if (newPosition < 0) {
        newPosition = 8;
      }
      setLeftPosition(newPosition);
    }
  };
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth;
    }
  }, []);
  useEffect(() => {
    if (!activeDropdown) return;
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (breadcrumbsRootRef.current?.contains(target)) return;
      setActiveDropdown(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [activeDropdown]);
  const handleMouseEnter = (step: IBreadcrumbStep) => {
    if (step.siblings && step.siblings.length > 0) {
      updateDropdownPosition(step.id);
      setTimeout(() => {
        setActiveDropdown(step);
      }, 100);
    } else {
      setActiveDropdown(null);
    }
  };
  const handleMouseLeave = (event: React.MouseEvent | MouseEvent) => {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const isMovingToDropdown = dropdownRef.current?.contains(relatedTarget);
    if (!isMovingToDropdown) {
      setActiveDropdown(null);
    }
  };
  const handleDropdownMouseLeave = (event: React.MouseEvent) => {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const isMovingToBreadcrumb = containerRef.current?.contains(relatedTarget);
    if (!isMovingToBreadcrumb) {
      setActiveDropdown(null);
    }
  };
  const handleClick = (step: IBreadcrumbStep, event: React.MouseEvent) => {
    if (step.id === 0) void router.push('/');
    if (activeDropdown?.id !== step.id) {
      handleMouseEnter(step);
    } else {
      handleMouseLeave(event);
    }
  };
  return <ErrorBoundary>
      <div ref={breadcrumbsRootRef} sx={styles.breadcrumbsContainer} data-cy="Breadcrumbs">
        <div ref={containerRef} sx={styles.breadcrumbContainer}>
          {steps.map((step: IBreadcrumbStep, index: number) => <BreadcrumbItem key={step.id} step={step} index={index} isLast={index === steps.length - 1} isActive={activeDropdown?.id === step.id} onMouseEnter={isTouchDevice ? () => null : () => handleMouseEnter(step)} onMouseLeave={isTouchDevice ? () => null : handleMouseLeave} onClick={event => handleClick(step, event)} />)}
        </div>
        <BreadcrumbDropdown activeDropdown={activeDropdown} leftPosition={leftPosition} onMouseLeave={handleDropdownMouseLeave} dropdownRef={dropdownRef} />
      </div>
    </ErrorBoundary>;
};
export default Breadcrumbs;